'use client'

import { useEffect, useRef, useState } from 'react'
import { ArrowUp, ChevronLeft, Heart, MoreVertical, Plus, Sparkles } from 'lucide-react'
import type { SiteCopy } from '../content'
import styles from './BettieConversation.module.css'

type Props = { copy: SiteCopy['about']['bettie']['demo']; suspended?: boolean }

export default function BettieConversation({ copy, suspended = false }: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(1)
  const [typing, setTyping] = useState(false)
  const [inView, setInView] = useState(false)
  const [pageVisible, setPageVisible] = useState(true)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [cycle, setCycle] = useState(0)

  useEffect(() => {
    const root = rootRef.current
    const observer = new IntersectionObserver(([entry]) => setInView(entry.intersectionRatio >= .25), { threshold: .25 })
    if (root) observer.observe(root)
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updateMotion = () => setReducedMotion(motion.matches)
    const updateVisibility = () => setPageVisible(!document.hidden)
    updateMotion()
    updateVisibility()
    motion.addEventListener('change', updateMotion)
    document.addEventListener('visibilitychange', updateVisibility)
    return () => {
      observer.disconnect()
      motion.removeEventListener('change', updateMotion)
      document.removeEventListener('visibilitychange', updateVisibility)
    }
  }, [])

  useEffect(() => {
    if (!inView || !pageVisible || reducedMotion || suspended) return
    const next = copy.messages[visibleCount]
    const previous = copy.messages[visibleCount - 1]
    const followUp = next?.sender === 'character' && previous?.sender === 'character'
    // Leave time to read the previous message; short follow-ups arrive as a burst.
    const delay = !next ? 8500
      : next.sender === 'user' ? Math.min(4200, 1600 + next.text.length * 38 + previous.text.length * 22)
      : followUp ? Math.min(2200, 850 + next.text.length * 35)
      : typing ? Math.min(2400, 950 + next.text.length * 42)
      : 550 + previous.text.length * 14
    const timer = window.setTimeout(() => {
      if (!next) {
        setVisibleCount(1)
        setTyping(false)
        setCycle(value => value + 1)
      } else if (next.sender === 'character' && !followUp && !typing) {
        setTyping(true)
      } else {
        setVisibleCount(count => count + 1)
        setTyping(false)
      }
    }, delay)
    return () => window.clearTimeout(timer)
  }, [copy.messages, visibleCount, typing, inView, pageVisible, reducedMotion, suspended, cycle])

  useEffect(() => {
    const viewport = messagesRef.current
    if (viewport && !reducedMotion) viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' })
  }, [visibleCount, typing, reducedMotion, cycle])

  const visibleMessages = copy.messages.slice(0, reducedMotion ? copy.messages.length : visibleCount)

  return (
    <div ref={rootRef} className={styles.preview} role="group" aria-label={copy.label}>
      <div className={styles.phone} aria-hidden="true" data-paused={!inView || !pageVisible || suspended}>
        <div className={styles.header}>
          <span className={`${styles.headerIcon} ${styles.back}`}><ChevronLeft size={23} /></span>
          <div className={styles.identity}><strong>{copy.name}</strong><span>{copy.status}<i /></span></div>
          <span className={styles.headerIcon}><Sparkles size={18} fill="currentColor" /></span>
          <span className={`${styles.headerIcon} ${styles.heart}`}><span className={styles.heartBalance}>9,650</span><Heart size={20} fill="currentColor" /></span>
          <span className={`${styles.headerIcon} ${styles.more}`}><MoreVertical size={19} /></span>
        </div>
        <div className={styles.messages} ref={messagesRef}>
          <p className={styles.date}>{copy.today}</p>
          {visibleMessages.map((message, index) => {
            const mine = message.sender === 'user'
            const startsGroup = visibleMessages[index - 1]?.sender !== message.sender
            const endsGroup = visibleMessages[index + 1]?.sender !== message.sender
            return <div key={`${cycle}-${index}`} className={`${styles.message} ${mine ? styles.outgoing : styles.incoming}`} data-group-end={endsGroup}>
              {startsGroup && <span className={styles.sender}>{mine ? copy.you : copy.name}</span>}
              <div className={styles.messageRow}>
                {!mine && <span className={endsGroup ? styles.avatar : styles.avatarSpace} />}
                {mine && endsGroup && <span className={styles.time}>{copy.time}</span>}
                <p className={styles.bubble}>{message.text}</p>
                {!mine && endsGroup && <span className={styles.time}>{copy.time}</span>}
              </div>
            </div>
          })}
          {typing && !reducedMotion && <div className={`${styles.message} ${styles.incoming}`}>
            <div className={styles.messageRow}><span className={styles.avatar} /><div className={styles.typing}><i /><i /><i /></div></div>
          </div>}
        </div>
        <div className={styles.phoneFooter}>
          <div className={styles.composer}><span className={styles.add}><Plus size={25} /></span><div className={styles.inputPlaceholder}><span>{copy.placeholder}</span><span className={styles.send}><ArrowUp size={18} /></span></div></div>
        </div>
      </div>
      <ol className={styles.transcript} aria-label={copy.label}>
        {copy.messages.map((message, index) => <li key={index}>{message.sender === 'user' ? copy.you : copy.name}: {message.text}</li>)}
      </ol>
    </div>
  )
}
